"use client"

// Full-screen background carousel for the sign-in screen.
// The source images already have a darkening overlay baked in by design,
// so this component does NOT add any extra overlay — it only cross-fades
// between the slides underneath the login card.

import { useEffect, useState } from "react"
import slide1 from "@/assets/login-carousel/slide-1.jpg"
import slide2 from "@/assets/login-carousel/slide-2.jpg"
import slide3 from "@/assets/login-carousel/slide-3.jpg"
import slide4 from "@/assets/login-carousel/slide-4.jpg"
import slide5 from "@/assets/login-carousel/slide-5.jpg"
import slide6 from "@/assets/login-carousel/slide-6.jpg"
import slide7 from "@/assets/login-carousel/slide-7.jpg"

const SLIDES = [slide1, slide2, slide3, slide4, slide5, slide6, slide7]
const INTERVAL_MS = 2000

/**
 * Decorative background: cross-fades through the login photos.
 *
 * Honors `prefers-reduced-motion`: when the user opts out of motion, the
 * carousel stays on the first slide instead of auto-advancing.
 */
export function LoginCarousel() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    // Guard matchMedia: it's absent in non-browser environments (e.g. jsdom in
    // tests). When unavailable, default to animating.
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (prefersReducedMotion) return

    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % SLIDES.length)
    }, INTERVAL_MS)

    return () => window.clearInterval(id)
  }, [])

  return (
    <div
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden bg-black"
      aria-hidden="true"
    >
      {SLIDES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          loading={i === 0 ? "eager" : "lazy"}
          decoding="async"
        />
      ))}
    </div>
  )
}
